/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { getToken as getFirebaseToken, onMessage } from "firebase/messaging";
import { messaging } from "./FirebaseConfig";
import { saveDeviceToken } from "../core/_requests";

//  Check notification permission
 const isNotificationEnabled = (): boolean => {
  return Notification.permission === "granted";
};

//  Request permission + Save token in localStorage
const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    const permission = await Notification.requestPermission();

    if (permission !== "granted") {
      console.warn(" Notifications are disabled by the user.");
      return null;
    }

    const token = await getFirebaseToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });

    if (token) {
      localStorage.setItem("fcmToken", token);
      return token;
    }

    console.warn(" Unable to retrieve Firebase token.");
    return null;
  } catch (error) {
    console.error(" Error requesting notification permission:", error);
    return null;
  }
};

//  Listen for foreground FCM messages
const onMessageListener = (showPopup?: Function) => {
  return new Promise<void>((resolve) => {
    onMessage(messaging, (payload: any) => {
      console.log(" FCM Foreground Message:", payload);

      // Play custom sound
      if (payload?.data?.soundUrl) {
        const audio = new Audio(payload.data.soundUrl);
        audio.play().catch((err) => console.error(" Audio error:", err));
      }

      // Browser Notification
      if (payload?.notification && isNotificationEnabled()) {
        new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: payload.notification.icon,
        });
      }

      // (Optional) Show App UI Popup/Toast
      if (showPopup) showPopup(payload);

      resolve();
    });
  });
};

//  Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent.toLowerCase();

  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (ua.includes("edg")) return "edge";
  if (ua.includes("chrome") && !ua.includes("edg") && !ua.includes("opr"))
    return "chrome";
  if (ua.includes("firefox")) return "firefox";
  if (ua.includes("safari") && !ua.includes("chrome")) return "safari";

  return "web";
};

// 📌 Save token to server only if notifications enabled
const saveFcmTokenToServer = async ({
  userId,
  userType,
  appVersion,
}: {
  userId: string;
  userType: string;
  appVersion: string;
}) => {
  try {
    // 👇 Detect + inform parent to show message
    if (!isNotificationEnabled()) {
      console.warn(" Notifications OFF — Not saving token.");
      alert("Please enable notifications to get your live order updates!");
      return;
    }

    let deviceToken = localStorage.getItem("fcmToken");

    if (!deviceToken) {
      console.log("🔄 Requesting fresh FCM token...");
      deviceToken = await requestNotificationPermission();
    }

    if (!deviceToken) {
      console.warn("❌ No FCM token. Save skipped.");
      return;
    }

    const deviceType = getDeviceType();

    await saveDeviceToken({
      userId,
      userType,
      deviceToken,
      deviceType,
      appVersion,
    });

    console.log("📌 Device token saved successfully!");
  } catch (error) {
    console.error("❌ Error saving device token:", error);
  }
};

export {
  requestNotificationPermission,
  onMessageListener,
  saveFcmTokenToServer,
  isNotificationEnabled,
};
